import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import GatsbyImage from 'gatsby-image'
import { useInView } from 'react-intersection-observer'
import { LayoutContext } from '../../contexts'
import {
  useGuaranteeClickedTracking,
  useModuleViewedTracking,
} from '../../hooks/tracking'

const Guarantee = ({
  guaranteeImage,
  guaranteeCopy,
  guaranteePeriod,
  guaranteeLink,
  guaranteePoints,
  viewType,
  position,
}) => {
  const { node_locale, currency } = useContext(LayoutContext)

  const viewTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [ref, inView] = isIE ? [] : useInView({ triggerOnce: true })

  useEffect(() => {
    if (!isIE) {
      if (inView) {
        useModuleViewedTracking({
          currency: currency?.code,
          locale: node_locale,
          sectionType: 'Guarantee',
          order: position,
        })
      }
    }
  }, [!isIE && inView])

  return (
    <StyledGuarantee ref={ref}>
      <ImageWrapper>
        <GatsbyImage fluid={guaranteeImage.fluid} alt='stox' />
        <GuaranteePeriod>{guaranteePeriod}</GuaranteePeriod>
      </ImageWrapper>
      <TextWrapper>
        <GuaranteeHeader>{guaranteeCopy}</GuaranteeHeader>
        <GuaranteeList>
          {guaranteePoints.map((data) => (
            <GuaranteeItem key={data}>{data}</GuaranteeItem>
          ))}
        </GuaranteeList>
        <StyledButton
          onClick={() => {
            viewTop()
            useGuaranteeClickedTracking({
              currency: currency?.code,
              locale: node_locale,
              linkType: 'Button',
              viewType: viewType,
              title: guaranteeCopy,
              label: guaranteeLink.title,
            })
          }}
        >
          {guaranteeLink.title}
        </StyledButton>
      </TextWrapper>
    </StyledGuarantee>
  )
}

const StyledGuarantee = styled.section`
  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-top: 1rem;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    padding: 0 ${(props) => props.theme.space.marginDesktop * 11.25}vw
      ${(props) => props.theme.space.marginDesktop * 10}vw
      ${(props) => props.theme.space.marginDesktop * 11.25}vw;
  }
`

const ImageWrapper = styled.div`
  z-index: -1;
  position: relative;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    flex: 2;

    .gatsby-image-wrapper {
      height: 100%;
    }
  }
`

const GuaranteePeriod = styled.p`
  color: ${(props) => props.theme.colors.white};
  font-size: 20vw;
  text-align: center;
  width: 100%;
  position: absolute;
  top: 30%;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 10.5vw;
    top: 28%;
    white-space: nowrap;
  }
`

const TextWrapper = styled.div`
  background-color: ${(props) => props.theme.colors.blue};
  color: white;
  padding: ${(props) => props.theme.space.marginMobile * 3}vw
    ${(props) => props.theme.space.marginMobile * 3}vw;
  margin: -32vw 12vw 12vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    flex: 1;
    padding: ${(props) => props.theme.space.marginDesktop * 5}vw
      ${(props) => props.theme.space.marginDesktop * 10}vw;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    margin-bottom: 7.4vw;
  }
`

const GuaranteeHeader = styled.h2`
  font-size: ${(props) => props.theme.fontSize.m35};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  line-height: 1.2;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d35};
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    width: 30vw;
  }
`

const GuaranteeList = styled.ul`
  list-style-type: circle;
  margin-left: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-left: ${(props) => props.theme.space.marginDesktop * 1.5}vw;
  }
`

const GuaranteeItem = styled.li`
  font-size: ${(props) => props.theme.fontSize.m23};
  padding-bottom: ${(props) => props.theme.space.marginMobile * 0.5}vw;
  line-height: 1.2;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
  }
`

const StyledButton = styled.button`
  cursor: pointer;
  background: white;
  color: ${(props) => props.theme.colors.blue};
  display: inline-block;
  padding: ${(props) => props.theme.space.marginMobile}vw
    ${(props) => props.theme.space.marginMobile * 2}vw;
  font-size: ${(props) => props.theme.fontSize.m20};
  font-family: ${(props) => props.theme.fontFamily.medium};
  margin-top: ${(props) => props.theme.space.marginMobile}vw;
  text-decoration: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    margin-top: ${(props) => props.theme.space.marginDesktop * 2}vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw
      ${(props) => props.theme.space.marginDesktop * 4}vw;
    width: fit-content;
  }
`

export default Guarantee
